import Link from 'next/link';

import { SUPPORT_TG_URL } from '@/app/config/const';
import { CareerOfferTrackedLink } from '@/features/career-offer-analytics';
import { Container } from '@/shared';
import { userAgreementDocument } from '@/shared/constants';
import { cn } from '@/shared/utils/cn';

const FooterTitle = ({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) => {
	return (
		<h3 className={cn('text-base font-semibold', className)}>{children}</h3>
	);
};

type FooterProps = {
	variant?: 'default' | 'dark';
	showCareerOfferLink?: boolean;
};

export const Footer = ({
	variant = 'default',
	showCareerOfferLink = false,
}: FooterProps) => {
	const isDark = variant === 'dark';

	return (
		<footer
			className={cn(
				'mt-auto py-8',
				isDark && 'border-t border-[#1e293b] bg-[#020617]',
				!isDark && 'bg-gray-4',
			)}
		>
			<Container className="space-y-8" full>
				{/* Основная информация и документы в трех колонках */}
				<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
					{/* Колонка 1: Данные организации */}
					<div className="space-y-4">
						<FooterTitle
							className={cn(isDark ? 'text-slate-100' : 'text-gray-1')}
						>
							Данные организации
						</FooterTitle>
						<div
							className={cn(
								'space-y-2 text-sm',
								isDark ? 'text-slate-400' : 'text-gray-3',
							)}
						>
							<div>ООО «Отус Онлайн-Образование»</div>
							<div>ОГРН 1177746618576</div>
							<div>ИНН 9705100963</div>
							<Link
								href={SUPPORT_TG_URL}
								target="_blank"
								rel="noopener noreferrer"
								className={cn(
									'inline-block hover:underline',
									isDark && 'text-slate-400 hover:text-slate-200',
								)}
							>
								Поддержка
							</Link>
						</div>
					</div>

					{/* Колонка 2: Лицензия */}
					<div className="space-y-4">
						<FooterTitle
							className={cn(isDark ? 'text-slate-100' : 'text-gray-1')}
						>
							Лицензия
						</FooterTitle>
						<div
							className={cn(
								'space-y-2 text-sm',
								isDark ? 'text-slate-400' : 'text-gray-3',
							)}
						>
							<Link
								href="https://s3.twcstorage.ru/algocode-static/docs/Otus/%D0%9B%D0%B8%D1%86%D0%B5%D0%BD%D0%B7%D0%B8%D1%8F%20%2B%20%D0%9F%D1%80%D0%B8%D0%BB%D0%BE%D0%B6%D0%B5%D0%BD%D0%B8%D0%B5%20%E2%84%961.1.pdf"
								target="_blank"
								className={cn(
									'block hover:underline',
									isDark
										? 'text-slate-400 hover:text-slate-200'
										: 'text-gray-3',
								)}
							>
								<div>
									Лицензия на осуществление образовательной деятельности
								</div>
								<div>№ 039825 от 28.12.2018</div>
							</Link>
						</div>
						{showCareerOfferLink && (
							<div className="space-y-4 pt-4">
								<FooterTitle
									className={cn(isDark ? 'text-slate-100' : 'text-gray-1')}
								>
									Программы
								</FooterTitle>
								<CareerOfferTrackedLink
									placement="problems_footer"
									entrypoint="promo"
									className={cn(
										'block text-sm hover:underline',
										isDark
											? 'text-slate-400 hover:text-slate-200'
											: 'text-gray-3',
									)}
								>
									Программа до оффера
								</CareerOfferTrackedLink>
							</div>
						)}
					</div>

					{/* Колонка 3: Основные документы */}
					<div className="space-y-4">
						<FooterTitle
							className={cn(isDark ? 'text-slate-100' : 'text-gray-1')}
						>
							Основные документы
						</FooterTitle>
						<div className="space-y-2 text-sm">
							<Link
								href="https://otus.ru/legal/common/"
								target="_blank"
								rel="noopener noreferrer"
								className={cn(
									'block hover:underline',
									isDark
										? 'text-slate-400 hover:text-slate-200'
										: 'text-gray-3',
								)}
							>
								Сведения об образовательной организации
							</Link>
							<Link
								href="https://otus.ru/legal/it_company_accreditation/"
								target="_blank"
								rel="noopener noreferrer"
								className={cn(
									'block hover:underline',
									isDark
										? 'text-slate-400 hover:text-slate-200'
										: 'text-gray-3',
								)}
							>
								OTUS является аккредитованной IT-компанией
							</Link>
							<Link
								href="https://reestr.digital.gov.ru/reestr/2704482/"
								target="_blank"
								rel="noopener noreferrer"
								className={cn(
									'block hover:underline',
									isDark
										? 'text-slate-400 hover:text-slate-200'
										: 'text-gray-3',
								)}
							>
								В реестре отечественного ПО №24216
							</Link>
							<Link
								href={userAgreementDocument.url}
								target="_blank"
								className={cn(
									'block hover:underline',
									isDark
										? 'text-slate-400 hover:text-slate-200'
										: 'text-gray-3',
								)}
							>
								Условия использования сервиса
							</Link>
							<Link
								href="https://otus.ru/legal/recommendations/"
								target="_blank"
								rel="noopener noreferrer"
								className={cn(
									'block hover:underline',
									isDark
										? 'text-slate-400 hover:text-slate-200'
										: 'text-gray-3',
								)}
							>
								Сведения о рекомендательных технологиях
							</Link>
						</div>
					</div>
				</div>
			</Container>
		</footer>
	);
};
